import { PersonList } from '@insurance/api/requests'
import { AddPersonButton, InputDatePicker } from '@insurance/components'
import { useValidationsField } from '@insurance/hooks'
import {
  Button,
  Card,
  DatePickerProps,
  Flex,
  Form,
  Space,
  Typography
} from 'antd'
import dayjs from 'dayjs'
import { Add, Trash } from 'iconsax-react'
import { useTranslation } from 'react-i18next'

import { useHealthInsuranceValues } from './HealthInsuranceContext'
import { useStyles } from './styles'
import { MIN_AGE_HEALTH_INSURANCE } from './utils'

const DynamicField = ({
  buttonDisabled,
  onRemove,
  ...props
}: DatePickerProps & { buttonDisabled?: boolean; onRemove: () => void }) => {
  return (
    <Space.Compact>
      <InputDatePicker {...props} />

      <Button
        icon={<Trash size={14} />}
        type="primary"
        disabled={buttonDisabled}
        onClick={() => onRemove()}
      />
    </Space.Compact>
  )
}

export const InsuredPersons = () => {
  const { t } = useTranslation()
  const { styles } = useStyles()
  const formInstance = Form.useFormInstance()
  const { isEditable } = useHealthInsuranceValues()
  const { required } = useValidationsField()

  const values = Form.useWatch('persons', { preserve: true }) as PersonList[]

  const handleAddPerson = (persons?: PersonList[]) => {
    const filteredValues = values?.filter((value) => !value?.id)

    const filteredPersons =
      persons?.map(({ ...person }) => ({
        ...person,
        date_of_birth: dayjs(person.date_of_birth).isValid()
          ? dayjs(person.date_of_birth)
          : undefined,
        document: [person.document?.series, person.document?.number]
          .filter(Boolean)
          .join(''),
        resident: 'resident' in person ? person.resident : true
      })) || []

    formInstance.setFieldValue('persons', [
      ...filteredValues,
      ...filteredPersons
    ])
  }

  return (
    <>
      <Flex
        align="center"
        gap={12}
        justify="space-between"
        style={{ paddingBottom: 12 }}
      >
        <Typography>{t('insured_persons')}</Typography>

        <AddPersonButton
          disabled={isEditable}
          personType="person"
          type="link"
          icon={<Add size={16} />}
          multiple
          value={values?.filter((value) => value?.id)}
          onAdd={handleAddPerson}
        >
          {t('add_existing_person')}
        </AddPersonButton>
      </Flex>

      <Form.List name="persons">
        {(fields, { add, remove }) => (
          <>
            <Flex wrap gap={12} className={styles.mb12}>
              {fields.map((field) => (
                <Card size="small" key={field.name}>
                  <Form.Item
                    label={t('date_of_birth')}
                    name={[field.name, 'date_of_birth']}
                    rules={[required]}
                  >
                    <DynamicField
                      disabled={isEditable}
                      maxDate={dayjs().subtract(
                        MIN_AGE_HEALTH_INSURANCE,
                        'year'
                      )}
                      buttonDisabled={fields?.length === 1}
                      allowClear={false}
                      onRemove={() => remove(field.name)}
                    />
                  </Form.Item>
                </Card>
              ))}
            </Flex>
            <Button
              disabled={isEditable}
              type="primary"
              onClick={() => add({ resident: true })}
            >
              {t('add_new_person')}
            </Button>
          </>
        )}
      </Form.List>
    </>
  )
}
